import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

interface OtpFormProps {
  username: string;
  onSuccess: () => void;
  onBack: () => void;
}

export function OtpForm({ username, onSuccess, onBack }: OtpFormProps) {
  const { t } = useTranslation();
  const [otp, setOtp] = useState('');
  const [method, setMethod] = useState<'email' | 'sms'>('email');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { requestOtp, verifyOtp } = useAuth();

  const handleRequestOtp = async () => {
    setError(null);
    try {
      await requestOtp(username, method);
      setSent(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await verifyOtp(username, otp);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="card-title">{t('auth.otp_login')}</h3>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {!sent ? (
        <>
          <p className="text-muted">
            {t('auth.otp_description')}
          </p>

          <div className="mb-3">
            <label className="form-label">{t('auth.delivery_method')}</label>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="method"
                id="method-email"
                value="email"
                checked={method === 'email'}
                onChange={(e) => setMethod(e.target.value as 'email' | 'sms')}
              />
              <label className="form-check-label" htmlFor="method-email">
                {t('auth.email')}
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="method"
                id="method-sms"
                value="sms"
                checked={method === 'sms'}
                onChange={(e) => setMethod(e.target.value as 'email' | 'sms')}
              />
              <label className="form-check-label" htmlFor="method-sms">
                {t('auth.sms')}
              </label>
            </div>
          </div>

          <button type="button" onClick={handleRequestOtp} className="btn btn-primary w-100">
            {t('auth.send_otp')}
          </button>
        </>
      ) : (
        <>
          <p className="text-muted">
            {t('auth.enter_otp', { method: t(method === 'email' ? 'auth.email' : 'auth.sms') })}
          </p>

          <div className="mb-3">
            <label htmlFor="otp" className="form-label">{t('auth.otp_code')}</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-control"
              id="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              maxLength={6}
              placeholder="123456"
            />
          </div>

          <button type="submit" className="btn btn-primary w-100">
            {t('auth.verify_otp')}
          </button>
        </>
      )}

      <div className="mt-3 text-center">
        <button
          type="button"
          className="btn btn-link"
          onClick={onBack}
        >
          {t('auth.back_to_password')}
        </button>
      </div>
    </form>
  );
}
